'use client';

import { useMemo, useState } from 'react';
import { sortRows, type SortDirection, type SortValue } from '@/lib/sort';

export function useSort<T, K extends string>(
  rows: readonly T[] | undefined,
  opts: {
    accessors: Record<K, (row: T) => SortValue>;
    initial: { key: K; direction: SortDirection };
    defaultDirections?: Partial<Record<K, SortDirection>>;
  },
) {
  const [sortKey, setSortKey] = useState<K>(opts.initial.key);
  const [direction, setDirection] = useState<SortDirection>(opts.initial.direction);
  const accessor = opts.accessors[sortKey];
  // Accessors must be pure functions of the row: they are excluded from the deps below.
  const sorted = useMemo(
    () => (rows ? sortRows(rows, accessor, direction) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, sortKey, direction],
  );
  const toggle = (key: K) => {
    if (key === sortKey) setDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setDirection(opts.defaultDirections?.[key] ?? 'asc');
    }
  };
  return { sorted, sortKey, direction, toggle };
}
